/**
 * Hero Section Component
 *
 * React implementation of the hero section renderer.
 * Supports all variants: standard, split, video, minimal, fullbleed
 */

import { HeroData, SectionProps, EditableElement as EditableElementType } from '../types';
import { EditableText, EditableImage } from '../components/EditableElement';
import { useEditableElement } from '../context';

// =============================================================================
// TYPES
// =============================================================================

interface HeroProps extends SectionProps<HeroData> {}

// =============================================================================
// HELPER: Find element by path
// =============================================================================

function findElement(elements: EditableElementType[] | undefined, path: string): EditableElementType | undefined {
  return elements?.find(el => el.path === path);
}

// =============================================================================
// HELPER: Background Image Edit Button
// =============================================================================

function BackgroundImageEditButton({ element }: { element: EditableElementType }) {
  const { handleClick } = useEditableElement(element);

  return (
    <button
      onClick={handleClick}
      className="absolute top-4 right-4 z-30 flex items-center gap-2 px-3 py-2 bg-white/90 hover:bg-white rounded-lg shadow-lg transition-all text-sm font-medium text-gray-700 hover:text-gray-900"
      title="Change background image"
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
      Edit Background
    </button>
  );
}

// =============================================================================
// HELPER: Rating Badge
// =============================================================================

function RatingBadge({
  rating,
  darkText = false,
}: {
  rating: HeroData['rating'];
  darkText?: boolean;
}) {
  if (!rating) return null;

  const textClass = darkText ? 'text-gray-900' : 'text-white';
  const mutedClass = darkText ? 'text-gray-600' : 'text-white/70';

  return (
    <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-full mb-6 ${darkText ? 'bg-yellow-50' : 'bg-white/10 backdrop-blur-sm'}`}>
      <div className="flex items-center gap-1">
        {Array.from({ length: 5 }, (_, i) => (
          <svg
            key={i}
            className={`w-5 h-5 ${i < Math.floor(rating.value) ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
      <span className={`font-semibold ${textClass}`}>{rating.value.toFixed(1)}</span>
      <span className={`${mutedClass} text-sm`}>
        ({rating.count.toLocaleString()} reviews{rating.platform ? ` on ${rating.platform}` : ''})
      </span>
    </div>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function Hero({ data, ctx, sectionId, elements }: HeroProps) {
  const variant = data.variant || 'standard';

  switch (variant) {
    case 'split':
      return <HeroSplit data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'video':
      return <HeroVideo data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'minimal':
      return <HeroMinimal data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    case 'fullbleed':
      return <HeroFullbleed data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
    default:
      return <HeroStandard data={data} ctx={ctx} sectionId={sectionId} elements={elements} />;
  }
}

// =============================================================================
// STANDARD HERO
// =============================================================================

function HeroStandard({ data, ctx, elements }: HeroProps) {
  const { recipe } = ctx;
  const bgImage = data.backgroundImage || '';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const eyebrowEl = findElement(elements, 'eyebrow');
  const bgImageEl = findElement(elements, 'backgroundImage');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  const { editMode } = ctx;

  return (
    <section id="hero" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background */}
      {bgImage ? (
        <div className="absolute inset-0 z-0">
          {bgImageEl ? (
            <EditableImage
              element={bgImageEl}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <img
              src={bgImage}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70 pointer-events-none" />
          {/* Edit button for background image */}
          {editMode && bgImageEl && (
            <BackgroundImageEditButton element={bgImageEl} />
          )}
        </div>
      ) : (
        <div
          className="absolute inset-0 z-0"
          style={{ background: `linear-gradient(135deg, ${recipe.colors.primary} 0%, ${recipe.colors.primaryDark} 100%)` }}
        />
      )}

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        {data.eyebrow && eyebrowEl && (
          <EditableText
            element={eyebrowEl}
            as="p"
            className="text-sm md:text-base font-semibold tracking-wider uppercase mb-4 text-white/80"
          />
        )}
        {data.eyebrow && !eyebrowEl && (
          <p className="text-sm md:text-base font-semibold tracking-wider uppercase mb-4 text-white/80">
            {data.eyebrow}
          </p>
        )}

        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h1"
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          />
        ) : (
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          >
            {data.headline}
          </h1>
        )}

        {data.subheadline && subheadlineEl && (
          <EditableText
            element={subheadlineEl}
            as="p"
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8"
            style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
          />
        )}
        {data.subheadline && !subheadlineEl && (
          <p
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8"
            style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
          >
            {data.subheadline}
          </p>
        )}

        {data.rating && <RatingBadge rating={data.rating} />}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          {data.primaryCta && (
            <a
              href={data.primaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
                boxShadow: recipe.shadows.button,
              }}
            >
              {primaryCtaTextEl ? (
                <EditableText element={primaryCtaTextEl} />
              ) : (
                data.primaryCta.text
              )}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          )}

          {data.secondaryCta && (
            <a
              href={data.secondaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20"
              style={{ borderRadius: recipe.borders.buttonRadius }}
            >
              {secondaryCtaTextEl ? (
                <EditableText element={secondaryCtaTextEl} />
              ) : (
                data.secondaryCta.text
              )}
            </a>
          )}
        </div>

        {/* Trust Badges */}
        {data.trustBadges && data.trustBadges.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
            {data.trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-white/70">
                {badge.icon ? (
                  <span className="text-xl">{badge.icon}</span>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a href="#services" className="text-white/60 hover:text-white transition-colors">
          <svg className="w-8 h-8 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </a>
      </div>
    </section>
  );
}

// =============================================================================
// SPLIT HERO
// =============================================================================

function HeroSplit({ data, ctx, elements }: HeroProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-white';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-300' : 'text-gray-600';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const eyebrowEl = findElement(elements, 'eyebrow');
  const splitImageEl = findElement(elements, 'splitImage');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  return (
    <section id="hero" className={`relative min-h-[90vh] flex items-center ${bgClass}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            {data.eyebrow && eyebrowEl && (
              <EditableText
                element={eyebrowEl}
                as="p"
                className="text-sm font-semibold tracking-wider uppercase mb-4"
                style={{ color: recipe.colors.primary }}
              />
            )}
            {data.eyebrow && !eyebrowEl && (
              <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
                {data.eyebrow}
              </p>
            )}

            {headlineEl ? (
              <EditableText
                element={headlineEl}
                as="h1"
                className={`text-4xl md:text-5xl lg:text-6xl font-${recipe.typography.headingWeight} ${textClass} mb-6 leading-tight`}
                style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
              />
            ) : (
              <h1
                className={`text-4xl md:text-5xl lg:text-6xl font-${recipe.typography.headingWeight} ${textClass} mb-6 leading-tight`}
                style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
              >
                {data.headline}
              </h1>
            )}

            {data.subheadline && subheadlineEl && (
              <EditableText
                element={subheadlineEl}
                as="p"
                className={`text-lg md:text-xl ${mutedClass} mb-8`}
                style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
              />
            )}
            {data.subheadline && !subheadlineEl && (
              <p className={`text-lg md:text-xl ${mutedClass} mb-8`} style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}>
                {data.subheadline}
              </p>
            )}

            {data.rating && <RatingBadge rating={data.rating} darkText={!isDark} />}

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4 mt-8">
              {data.primaryCta && (
                <a
                  href={data.primaryCta.url}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105 hover:opacity-90"
                  style={{
                    backgroundColor: recipe.colors.primary,
                    borderRadius: recipe.borders.buttonRadius,
                    boxShadow: recipe.shadows.button,
                  }}
                >
                  {primaryCtaTextEl ? (
                    <EditableText element={primaryCtaTextEl} />
                  ) : (
                    data.primaryCta.text
                  )}
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
              )}

              {data.secondaryCta && (
                <a
                  href={data.secondaryCta.url}
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold transition-all"
                  style={{
                    color: recipe.colors.primary,
                    border: `2px solid ${recipe.colors.primary}`,
                    borderRadius: recipe.borders.buttonRadius,
                  }}
                >
                  {secondaryCtaTextEl ? (
                    <EditableText element={secondaryCtaTextEl} />
                  ) : (
                    data.secondaryCta.text
                  )}
                </a>
              )}
            </div>

            {/* Trust Badges */}
            {data.trustBadges && data.trustBadges.length > 0 && (
              <div className={`flex flex-wrap items-center gap-6 mt-10 pt-10 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {data.trustBadges.map((badge, index) => (
                  <div key={index} className={`flex items-center gap-2 ${mutedClass}`}>
                    {badge.icon ? (
                      <span className="text-xl">{badge.icon}</span>
                    ) : (
                      <svg className="w-5 h-5" style={{ color: recipe.colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">{badge.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Image */}
          <div className="order-1 lg:order-2 relative">
            {data.splitImage ? (
              <div className="relative">
                <div className="absolute -inset-4 rounded-2xl opacity-20" style={{ background: recipe.colors.primary }} />
                {splitImageEl ? (
                  <EditableImage
                    element={splitImageEl}
                    alt={data.headline || ''}
                    className="relative w-full h-auto object-cover"
                    style={{
                      borderRadius: recipe.borders.imageRadius,
                      boxShadow: recipe.shadows.elevated,
                    }}
                    loading="eager"
                  />
                ) : (
                  <img
                    src={data.splitImage}
                    alt={data.headline || ''}
                    className="relative w-full h-auto object-cover"
                    style={{
                      borderRadius: recipe.borders.imageRadius,
                      boxShadow: recipe.shadows.elevated,
                    }}
                    loading="eager"
                  />
                )}
              </div>
            ) : (
              <div
                className="aspect-[4/3] rounded-2xl"
                style={{ background: `linear-gradient(135deg, ${recipe.colors.primary}20 0%, ${recipe.colors.secondary}20 100%)` }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// VIDEO HERO (simplified - no actual video in preview)
// =============================================================================

function HeroVideo({ data, ctx, elements }: HeroProps) {
  const { recipe } = ctx;

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  return (
    <section id="hero" className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Video placeholder */}
      <div className="absolute inset-0 z-0">
        {data.videoPoster ? (
          <img
            src={data.videoPoster}
            alt=""
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gray-800" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80 pointer-events-none" />
        {/* Video indicator */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20">
        {data.eyebrow && (
          <p className="text-sm md:text-base font-semibold tracking-wider uppercase mb-4 text-white/80">
            {data.eyebrow}
          </p>
        )}

        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h1"
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          />
        ) : (
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          >
            {data.headline}
          </h1>
        )}

        {data.subheadline && subheadlineEl && (
          <EditableText
            element={subheadlineEl}
            as="p"
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8"
            style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
          />
        )}
        {data.subheadline && !subheadlineEl && (
          <p className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8" style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}>
            {data.subheadline}
          </p>
        )}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          {data.primaryCta && (
            <a
              href={data.primaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
                boxShadow: recipe.shadows.button,
              }}
            >
              {primaryCtaTextEl ? (
                <EditableText element={primaryCtaTextEl} />
              ) : (
                data.primaryCta.text
              )}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          )}

          {data.secondaryCta && (
            <a
              href={data.secondaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20"
              style={{ borderRadius: recipe.borders.buttonRadius }}
            >
              {secondaryCtaTextEl ? (
                <EditableText element={secondaryCtaTextEl} />
              ) : (
                data.secondaryCta.text
              )}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

// =============================================================================
// MINIMAL HERO
// =============================================================================

function HeroMinimal({ data, ctx, elements }: HeroProps) {
  const { recipe } = ctx;
  const isDark = recipe.mode === 'dark';
  const bgClass = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const textClass = isDark ? 'text-white' : 'text-gray-900';
  const mutedClass = isDark ? 'text-gray-400' : 'text-gray-600';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const eyebrowEl = findElement(elements, 'eyebrow');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  return (
    <section id="hero" className={`relative py-20 md:py-32 ${bgClass}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {data.eyebrow && eyebrowEl && (
          <EditableText
            element={eyebrowEl}
            as="p"
            className="text-sm font-semibold tracking-wider uppercase mb-4"
            style={{ color: recipe.colors.primary }}
          />
        )}
        {data.eyebrow && !eyebrowEl && (
          <p className="text-sm font-semibold tracking-wider uppercase mb-4" style={{ color: recipe.colors.primary }}>
            {data.eyebrow}
          </p>
        )}

        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h1"
            className={`text-4xl md:text-5xl lg:text-6xl font-${recipe.typography.headingWeight} ${textClass} mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          />
        ) : (
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl font-${recipe.typography.headingWeight} ${textClass} mb-6 leading-tight`}
            style={{ fontFamily: `${recipe.typography.displayFont}, serif` }}
          >
            {data.headline}
          </h1>
        )}

        {data.subheadline && subheadlineEl && (
          <EditableText
            element={subheadlineEl}
            as="p"
            className={`text-lg md:text-xl ${mutedClass} max-w-2xl mx-auto mb-8`}
            style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}
          />
        )}
        {data.subheadline && !subheadlineEl && (
          <p className={`text-lg md:text-xl ${mutedClass} max-w-2xl mx-auto mb-8`} style={{ fontFamily: `${recipe.typography.bodyFont}, sans-serif` }}>
            {data.subheadline}
          </p>
        )}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {data.primaryCta && (
            <a
              href={data.primaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105 hover:opacity-90"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
                boxShadow: recipe.shadows.button,
              }}
            >
              {primaryCtaTextEl ? (
                <EditableText element={primaryCtaTextEl} />
              ) : (
                data.primaryCta.text
              )}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          )}

          {data.secondaryCta && (
            <a
              href={data.secondaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold transition-all"
              style={{
                color: recipe.colors.primary,
                border: `2px solid ${recipe.colors.primary}`,
                borderRadius: recipe.borders.buttonRadius,
              }}
            >
              {secondaryCtaTextEl ? (
                <EditableText element={secondaryCtaTextEl} />
              ) : (
                data.secondaryCta.text
              )}
            </a>
          )}
        </div>

        {/* Trust Badges */}
        {data.trustBadges && data.trustBadges.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
            {data.trustBadges.map((badge, index) => (
              <div key={index} className={`flex items-center gap-2 ${mutedClass}`}>
                {badge.icon ? (
                  <span className="text-xl">{badge.icon}</span>
                ) : (
                  <svg className="w-5 h-5" style={{ color: recipe.colors.primary }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// =============================================================================
// FULLBLEED HERO
// =============================================================================

function HeroFullbleed({ data, ctx, elements }: HeroProps) {
  const { recipe, editMode } = ctx;
  const bgImage = data.backgroundImage || '';

  const headlineEl = findElement(elements, 'headline');
  const subheadlineEl = findElement(elements, 'subheadline');
  const eyebrowEl = findElement(elements, 'eyebrow');
  const bgImageEl = findElement(elements, 'backgroundImage');
  const primaryCtaTextEl = findElement(elements, 'primaryCta.text');
  const secondaryCtaTextEl = findElement(elements, 'secondaryCta.text');

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden" data-hero-fullbleed="true">
      {/* Background */}
      {bgImage ? (
        <div className="absolute inset-0 z-0">
          {bgImageEl ? (
            <EditableImage
              element={bgImageEl}
              className="w-full h-full object-cover"
              loading="eager"
            />
          ) : (
            <img
              src={bgImage}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60 pointer-events-none" />
          {/* Edit button for background image */}
          {editMode && bgImageEl && (
            <BackgroundImageEditButton element={bgImageEl} />
          )}
        </div>
      ) : (
        <div
          className="absolute inset-0 z-0"
          style={{ background: `linear-gradient(135deg, ${recipe.colors.primary} 0%, ${recipe.colors.primaryDark} 100%)` }}
        />
      )}

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-20 pt-32">
        {data.eyebrow && eyebrowEl && (
          <EditableText
            element={eyebrowEl}
            as="p"
            className="text-sm md:text-base font-semibold tracking-wider uppercase mb-4 text-white/80"
          />
        )}
        {data.eyebrow && !eyebrowEl && (
          <p className="text-sm md:text-base font-semibold tracking-wider uppercase mb-4 text-white/80">
            {data.eyebrow}
          </p>
        )}

        {headlineEl ? (
          <EditableText
            element={headlineEl}
            as="h1"
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{
              fontFamily: `${recipe.typography.displayFont}, serif`,
              textShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          />
        ) : (
          <h1
            className={`text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-${recipe.typography.headingWeight} text-white mb-6 leading-tight`}
            style={{
              fontFamily: `${recipe.typography.displayFont}, serif`,
              textShadow: '0 4px 12px rgba(0,0,0,0.4)',
            }}
          >
            {data.headline}
          </h1>
        )}

        {data.subheadline && subheadlineEl && (
          <EditableText
            element={subheadlineEl}
            as="p"
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8"
            style={{
              fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          />
        )}
        {data.subheadline && !subheadlineEl && (
          <p
            className="text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto mb-8"
            style={{
              fontFamily: `${recipe.typography.bodyFont}, sans-serif`,
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            {data.subheadline}
          </p>
        )}

        {data.rating && <RatingBadge rating={data.rating} />}

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
          {data.primaryCta && (
            <a
              href={data.primaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white transition-all hover:scale-105"
              style={{
                backgroundColor: recipe.colors.primary,
                borderRadius: recipe.borders.buttonRadius,
                boxShadow: recipe.shadows.button,
              }}
            >
              {primaryCtaTextEl ? (
                <EditableText element={primaryCtaTextEl} />
              ) : (
                data.primaryCta.text
              )}
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
          )}

          {data.secondaryCta && (
            <a
              href={data.secondaryCta.url}
              className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 bg-white/10 backdrop-blur-sm transition-all hover:bg-white/20"
              style={{ borderRadius: recipe.borders.buttonRadius }}
            >
              {secondaryCtaTextEl ? (
                <EditableText element={secondaryCtaTextEl} />
              ) : (
                data.secondaryCta.text
              )}
            </a>
          )}
        </div>

        {/* Trust Badges */}
        {data.trustBadges && data.trustBadges.length > 0 && (
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
            {data.trustBadges.map((badge, index) => (
              <div key={index} className="flex items-center gap-2 text-white/70">
                {badge.icon ? (
                  <span className="text-xl">{badge.icon}</span>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                <span className="text-sm font-medium">{badge.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
        <a href="#services" className="text-white/60 hover:text-white transition-colors">
          <svg className="w-8 h-8 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </a>
      </div>
    </section>
  );
}

export default Hero;
